import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { apiClient } from '@/services/api';
import { AuthFormCard, AuthPageLayout } from '@/layouts/AuthPageLayout';
import { toast } from '../stores/toastStore';
import { AuthButton, AuthInput, AuthLabel, FieldError } from './auth/authFormControls';
import { validateEmail } from './auth/authValidation';

export const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleEmailChange = (value: string): void => {
    setEmail(value);
    if (emailError) setEmailError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateEmail(email);
    setEmailError(err);
    if (err) {
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.post('/auth/forgot-password', { email: email.trim() });
      setIsSuccess(true);
      toast.success('Password reset link sent. Check your email.');
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Something went wrong. Please try again.';
      toast.error(message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <AuthPageLayout>
        <AuthFormCard className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
          <h2 className="mb-2 text-2xl font-semibold text-gray-900">Check your email</h2>
          <p className="mb-6 text-sm text-gray-500">
            We've sent a password reset link to <span className="font-medium text-gray-900">{email}</span>
          </p>
          <Link to="/login">
            <AuthButton variant="outline" className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </AuthButton>
          </Link>
        </AuthFormCard>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <AuthFormCard>
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">Reset your password</h1>
          <p className="text-sm text-gray-500">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <div className="space-y-2">
            <AuthLabel htmlFor="email" required>Email address</AuthLabel>
            <AuthInput
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              error={!!emailError}
              disabled={isLoading}
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'email-error' : undefined}
            />
            <FieldError id="email-error" message={emailError} />
          </div>

          <AuthButton type="submit" className="w-full" loading={isLoading}>
            {isLoading ? 'Sending...' : 'Send reset link'}
          </AuthButton>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-gray-600 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Login
          </Link>
        </div>
      </AuthFormCard>
    </AuthPageLayout>
  );
};

export default ForgotPassword;
